import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { userRoleService } from '../../../../services/userRoleService';
import { UserRoles } from '../../../../types/userRole';
import './UserManagement.css';

const RolesAccessMatrix: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<UserRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await userRoleService.getUserRole();
      setRoles((data || []).slice().sort((a: UserRoles, b: UserRoles) => a.id - b.id));
    } catch (err) {
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const allPermissions = Array.from(
    new Set(roles.flatMap((r) => r.permissions || []))
  ).sort();

  const togglePermission = async (role: UserRoles, permission: string) => {
    const cellKey = `${role.id}:${permission}`;
    const hasIt = role.permissions.includes(permission);
    const newPermissions = hasIt
      ? role.permissions.filter((p) => p !== permission)
      : [...role.permissions, permission];

    // Optimistic update
    setRoles((prev) =>
      prev.map((r) => (r.id === role.id ? { ...r, permissions: newPermissions } : r))
    );
    setSavingCell(cellKey);
    setError(null);

    try {
      await userRoleService.updateRole(role.id, { permissions: newPermissions });
    } catch (err) {
      // Revert on failure
      setRoles((prev) =>
        prev.map((r) => (r.id === role.id ? { ...r, permissions: role.permissions } : r))
      );
      setError(`Failed to update "${permission}" for role "${role.name}"`);
    } finally {
      setSavingCell(null);
    }
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <div className="header-left">
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin/roles')} style={{ marginBottom: 10 }}>
            <ArrowLeft size={16} /> Back to Roles
          </button>
          <h1>Roles &amp; Access</h1>
          <p>Toggle exactly what each role can do. Changes save immediately.</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={fetchRoles}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {error && (
        <div className="empty-state" style={{ color: '#ef4444', textAlign: 'left', padding: '10px 0' }}>
          {error}
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <div className="loading-state">Loading roles...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="users-table role-table">
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Role</th>
                  {allPermissions.map((perm) => (
                    <th key={perm} style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{perm}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.id}>
                    <td className="role-matrix-name" style={{ position: 'sticky', left: 0, background: '#fff' }}>
                      <strong>{role.name}</strong>
                      {!role.is_active && (
                        <span className="badge badge-danger" style={{ marginLeft: 8, fontSize: 10 }}>INACTIVE</span>
                      )}
                    </td>
                    {allPermissions.map((perm) => {
                      const cellKey = `${role.id}:${perm}`;
                      const checked = role.permissions.includes(perm);
                      return (
                        <td key={perm} data-label={perm} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={savingCell === cellKey}
                            onChange={() => togglePermission(role, perm)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesAccessMatrix;
